import ReactDOM from 'react-dom';
import { Fragment, useEffect, useRef, useState } from 'react';
import { X, ArrowRight, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';

import { useGetAdvertChangeDiffQuery } from '../../../../redux/services/parser/parserApi';
import { Loader } from '../../Loader';
import { TextDiffView } from '../../TextDiffView';

const isDiffable = row => {
  const hasStringSide =
    typeof row?.old_value === 'string' || typeof row?.new_value === 'string';
  if (!hasStringSide) return false;
  const oldLen = row?.old_value == null ? 0 : String(row.old_value).length;
  const newLen = row?.new_value == null ? 0 : String(row.new_value).length;
  return oldLen + newLen > 0;
};

const renderValue = (display, raw) => {
  if (display === null || display === undefined || display === '') {
    return <span className="italic text-darkGrayColor">—</span>;
  }
  return <span title={raw ?? undefined}>{display}</span>;
};

export const AdvertDiffModal = ({ isOpen, onClose, token, eventId, advertPreview }) => {
  const portalRef = useRef(null);
  if (portalRef.current === null && typeof document !== 'undefined') {
    portalRef.current = document.createElement('div');
  }

  const [expandedFields, setExpandedFields] = useState(() => new Set());

  useEffect(() => {
    setExpandedFields(new Set());
  }, [eventId]);

  const toggleExpanded = field => {
    setExpandedFields(prev => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
  };

  useEffect(() => {
    const container = portalRef.current;
    if (!container) return undefined;
    document.body.appendChild(container);
    return () => {
      document.body.removeChild(container);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = e => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const { data, isLoading, isFetching, error } = useGetAdvertChangeDiffQuery(
    { token, eventId },
    { skip: !isOpen || !token || !eventId }
  );

  if (!isOpen || !portalRef.current) return null;

  const rows = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const advert = data?.advert ?? advertPreview ?? null;
  const task = data?.task ?? null;

  return ReactDOM.createPortal(
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="advert-diff-title"
      data-testid="advert-diff-modal"
      className="fixed top-1/2 right-4 -translate-y-1/2 z-20 w-[92vw] max-w-md max-h-[80vh] rounded-xl bg-white shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
    >
      <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-gray-200 bg-lightGrayColor/50 flex-shrink-0">
        <div className="min-w-0">
          <h3 id="advert-diff-title" className="text-sm font-semibold text-blackColor">
            What changed
          </h3>
          <div className="mt-0.5 text-[11px] text-darkGrayColor truncate">
            {advert?.title ? (
              <span title={advert.title}>{advert.title}</span>
            ) : (
              <span>Advert #{advert?.id}</span>
            )}
            {task?.source && (
              <span className="ml-1">
                · <span className="text-purpleColor">{task.source}</span>
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {advert?.url && (
            <a
              href={advert.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-purpleColor hover:underline"
              title={advert.url}
              data-testid="advert-diff-open-link"
            >
              Open <ExternalLink size={11} />
            </a>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close diff panel"
            data-testid="advert-diff-close"
            className="rounded-md p-1 text-darkGrayColor hover:text-blackColor hover:bg-lightGrayColor"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        {isLoading && (
          <div className="flex items-center justify-center h-40">
            <Loader />
          </div>
        )}

        {!isLoading && error && (
          <div
            data-testid="advert-diff-error"
            className="flex flex-col items-center justify-center h-40 text-xs text-redColor gap-1 px-6 text-center"
          >
            <span>Failed to load diff.</span>
            <span className="text-[11px] text-darkGrayColor">
              {error?.status ? `HTTP ${error.status}` : 'Network or server error'}
            </span>
          </div>
        )}

        {!isLoading && !error && total === 0 && (
          <div
            data-testid="advert-diff-empty"
            className="flex items-center justify-center h-40 text-xs text-darkGrayColor px-6 text-center"
          >
            No field-level changes recorded for this event.
          </div>
        )}

        {!isLoading && !error && total > 0 && (
          <table className="w-full text-left text-xs" data-testid="advert-diff-table">
            <thead className="sticky top-0 bg-white z-10 border-b border-gray-200 text-darkGrayColor uppercase tracking-wide">
              <tr>
                <th className="py-2 pl-4 pr-2 font-medium w-1/3">Field</th>
                <th className="py-2 px-2 font-medium">Before</th>
                <th className="py-2 pl-2 pr-4 font-medium">After</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map(row => {
                const diffable = isDiffable(row);
                const expanded = expandedFields.has(row.field);
                return (
                  <Fragment key={row.field}>
                    <tr
                      data-testid={`advert-diff-row-${row.field}`}
                      className={
                        'align-top ' +
                        (expanded ? 'bg-purpleColor/5' : 'hover:bg-lightGrayColor/40')
                      }
                    >
                      <td className="py-2 pl-4 pr-2 font-medium text-blackColor">
                        <div className="flex items-center gap-1">
                          {diffable && (
                            <button
                              type="button"
                              onClick={() => toggleExpanded(row.field)}
                              aria-label={
                                expanded
                                  ? `Hide diff for ${row.label}`
                                  : `Show diff for ${row.label}`
                              }
                              aria-expanded={expanded}
                              data-testid={`advert-diff-toggle-${row.field}`}
                              className="rounded p-0.5 text-purpleColor hover:bg-purpleColor/10 flex-shrink-0"
                              title={expanded ? 'Hide diff' : 'Show diff'}
                            >
                              {expanded ? (
                                <ChevronUp size={12} />
                              ) : (
                                <ChevronDown size={12} />
                              )}
                            </button>
                          )}
                          <span>{row.label}</span>
                        </div>
                      </td>
                      <td className="py-2 px-2 text-darkGrayColor">
                        <div className="inline-flex items-center gap-1">
                          <span className="line-through decoration-redColor/60">
                            {renderValue(row.old_display, row.old_value)}
                          </span>
                        </div>
                      </td>
                      <td className="py-2 pl-2 pr-4 text-blackColor">
                        <div className="inline-flex items-center gap-1">
                          <ArrowRight size={11} className="text-greenColor flex-shrink-0" />
                          <span className="font-medium">
                            {renderValue(row.new_display, row.new_value)}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {diffable && expanded && (
                      <tr
                        data-testid={`advert-diff-expanded-${row.field}`}
                        className="bg-purpleColor/5"
                      >
                        <td colSpan={3} className="px-3 pb-3 pt-1">
                          <TextDiffView
                            testId={`advert-diff-viewer-${row.field}`}
                            oldValue={row.old_value}
                            newValue={row.new_value}
                            splitView={false}
                            showDiffOnly={false}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="px-4 py-2 border-t border-gray-200 bg-lightGrayColor/30 text-[11px] text-darkGrayColor flex items-center justify-between flex-shrink-0">
        <span>
          {total.toLocaleString()} field{total === 1 ? '' : 's'} changed
        </span>
        {isFetching && !isLoading && <span>refreshing…</span>}
      </div>
    </div>,
    portalRef.current
  );
};
